import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";

// iter v29 r249 — Pagina Privacy standalone richiesta da Apple App Store
// Connect. Deve essere accessibile senza login a un URL stabile.
// Il contenuto è una copia esatta del tab "privacy" di /terms così
// l'App Store crawler riceve il testo direttamente, senza dipendere
// da redirect JS (alcuni check automatici Apple non seguono i redirect
// client-side delle SPA).

const COMPANY_LINE = "Pronto · per agenti immobiliari";
const SUPPORT_EMAIL = "supporto@prontoimmo.it";

const SECTIONS = [
  ["1. Titolare del trattamento",
    `Il Titolare del trattamento è ${COMPANY_LINE}, contattabile via email all'indirizzo ${SUPPORT_EMAIL}. Per i fini del GDPR, il referente è raggiungibile tramite il medesimo indirizzo.`],
  ["2. Dati raccolti",
    "Trattiamo le seguenti categorie di dati: (a) dati di registrazione: nome, email, password (memorizzata cifrata con bcrypt); (b) dati professionali: nome agenzia, telefono, logo, indirizzo (facoltativi); (c) dati di pagamento: gestiti integralmente da Stripe Inc., Pronto non memorizza numeri di carta o CVV; (d) contenuti caricati: foto degli immobili, planimetrie catastali, registrazioni vocali del sopralluogo, descrizioni; (e) dati di lead: contatti dei potenziali acquirenti che inviano richieste tramite la pagina pubblica dell'immobile (con consenso esplicito); (f) dati tecnici: indirizzo IP, user-agent, timestamp di accesso, log applicativi."],
  ["3. Finalità e base giuridica",
    "I dati sono trattati per: (a) erogare il servizio richiesto (base giuridica: esecuzione del contratto, art. 6.1.b GDPR); (b) emettere fatture e adempiere obblighi fiscali (obbligo di legge, art. 6.1.c); (c) inviare comunicazioni di servizio (consenso o legittimo interesse); (d) prevenire abusi e tentativi di frode (legittimo interesse, art. 6.1.f). NON utilizziamo i dati per profilazione né per marketing di terze parti."],
  ["4. Conservazione",
    "I contenuti caricati sono conservati per la durata dell'abbonamento e per ulteriori 30 giorni dopo la chiusura dell'account, dopodiché vengono cancellati definitivamente. I dati di fatturazione sono conservati per 10 anni come da normativa fiscale italiana. I log tecnici sono conservati per 90 giorni."],
  ["5. Server e trasferimenti",
    "I dati personali sono ospitati su server europei (Unione Europea o Svizzera). I provider di elaborazione utilizzati (Google, OpenAI, Anthropic) possono trattare i contenuti su server statunitensi: il trasferimento avviene sulla base delle Standard Contractual Clauses (SCC) approvate dalla Commissione Europea e degli accordi sul Privacy Framework UE-USA. Le foto inviate per l'elaborazione automatica non vengono utilizzate dai provider per addestrare i propri modelli."],
  ["6. Permessi del dispositivo (iOS)",
    "L'app iOS richiede i seguenti permessi solo quando strettamente necessari per la funzionalità scelta dall'utente: (a) Fotocamera — per scattare foto dell'immobile durante il sopralluogo; (b) Galleria foto — per caricare foto esistenti dell'immobile; (c) Microfono — per la dettatura vocale delle note di sopralluogo (trascritta localmente come testo, l'audio non viene archiviato); (d) Posizione — solo se l'agente sceglie di geo-taggare l'immobile, mai in background; (e) Notifiche — promemoria appuntamenti e nuovi lead. Tutti i permessi possono essere revocati in qualsiasi momento dalle Impostazioni iOS."],
  ["7. Diritti dell'utente",
    `L'utente ha il diritto di: accedere ai propri dati, chiederne la rettifica o cancellazione, ricevere copia dei dati in formato portabile, opporsi al trattamento, presentare reclamo al Garante Privacy (www.garanteprivacy.it). Per esercitare questi diritti, scrivere a ${SUPPORT_EMAIL}. La richiesta sarà evasa entro 30 giorni. Pronto offre la cancellazione integrale dell'account dalla pagina Profilo (effetto immediato).`],
  ["8. Sicurezza",
    "Tutte le comunicazioni sono protette da TLS 1.3. Le password sono salvate con bcrypt (costo 12). Gli accessi sono protetti da rate limiting per prevenire attacchi brute force. I backup del database sono cifrati a riposo."],
  ["9. Minori",
    "Il servizio è destinato esclusivamente a maggiorenni. Non raccogliamo consapevolmente dati di soggetti sotto i 16 anni."],
  ["10. Modifiche all'informativa",
    "Eventuali aggiornamenti saranno comunicati via email e con avviso sulla home del sito. Versione corrente: 9 giugno 2026."],
];

export default function Privacy() {
  return (
    <div className="min-h-screen bg-[#FFFFFF]" data-testid="privacy-page">
      <header className="glass-header sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 sm:h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="brand-link-privacy">
            <img
              src="/logo/full-with-tagline.png?v=2"
              alt="Pronto · per agenti immobiliari"
              className="h-12 sm:h-14 w-auto select-none"
              draggable="false"
            />
          </Link>
          <Link
            to="/"
            className="text-sm text-[#6B6A65] hover:text-[#7C8B6E] flex items-center gap-1.5"
            data-testid="privacy-home-link"
          >
            <ArrowLeft size={14} /> Home
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 lg:px-10 py-12 lg:py-20">
        <h1
          className="font-editorial text-4xl lg:text-5xl tracking-tight"
          data-testid="privacy-title"
        >
          Informativa sulla Privacy
        </h1>

        <p
          className="mt-8 text-[15px] text-[#6B6A65] leading-relaxed italic border-l-2 border-[#7C8B6E] pl-4"
          data-testid="privacy-intro"
        >
          Ai sensi del Regolamento UE 2016/679 (GDPR), del D.Lgs. 196/2003 come
          modificato dal D.Lgs. 101/2018, del Regolamento UE 2024/1689 (AI Act)
          e della Legge 23 settembre 2025 n. 132 in materia di intelligenza
          artificiale, {COMPANY_LINE} fornisce le seguenti informazioni sul
          trattamento dei dati personali.
        </p>

        <article className="mt-8 space-y-7 text-[16px] text-[#1C1C1A] leading-relaxed">
          {SECTIONS.map(([heading, body], i) => (
            <section key={i} data-testid={`privacy-section-${i + 1}`}>
              <h2 className="font-editorial text-xl text-[#1C1C1A] mb-2">{heading}</h2>
              <p>{body}</p>
            </section>
          ))}
        </article>

        <div className="mt-12 pt-6 border-t border-[#E5E7E0]">
          <p className="text-xs text-[#6B6A65]">
            Ultimo aggiornamento: 9 giugno 2026 · {COMPANY_LINE} · prontoimmo.it · {SUPPORT_EMAIL}
          </p>
          <div className="mt-4 flex flex-wrap gap-3 text-xs">
            <Link to="/terms" className="text-[#7C8B6E] hover:underline">
              Termini e Condizioni
            </Link>
            <span className="text-[#D5DAC9]">·</span>
            <Link to="/terms?tab=cookie" className="text-[#7C8B6E] hover:underline">
              Cookie Policy
            </Link>
            <span className="text-[#D5DAC9]">·</span>
            <Link to="/supporto" className="text-[#7C8B6E] hover:underline">
              Supporto
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
